import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  InboxOutlined,
  InfoCircleOutlined,
  ToolOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { DB } from "@p0security/frontend-core/react";
import { toAssessmentPath } from "@p0security/shared/assessment/helper";
import { AppPaths } from "@p0security/shared/routes/constants";
import {
  Button,
  Flex,
  List,
  Modal,
  Spacing,
  Tooltip,
  Typography,
  message,
} from "@p0security/ui";
import { SelectedEnvironmentContext } from "components/Environment/contexts/SelectedEnvironmentContext";
import { Tenant } from "components/Login";
import {
  ResizableTable,
  type ResizableTableColumnsType,
} from "components/ResizableTable";
import { AuthzButton } from "components/common/AuthzButton";
import { doc, writeBatch } from "firebase/firestore";
import { orderBy } from "lodash";
import { useCallback, useContext, useMemo, useState } from "react";
import { StyledTag } from "styles/StyledTags";
import { colors } from "styles/variables";

import {
  FindingsContext,
  MonitorWithCount,
  MonitorWithMetadata,
} from "../contexts/FindingsContext";
import { useNavigateWithEnv } from "../hooks/useNavigateWithEnv";
import { EditMonitorModal } from "./EditMonitorModal";
import { LinkWithEnvironment } from "./LinkWithEnvironment";
import { MonitorTypeIcon } from "./cells/MonitorTypeIcon";
import { P0Badge, P0BadgeSpacer } from "./cells/P0Badge";
import { MonitorPriority } from "./monitor/MonitorSeverity";

const MonitorActionButton: React.FC<{
  actionType: "archive" | "edit" | "template" | "unarchive" | "view";
  icon: React.ReactNode;
  tooltip: string;
  onClick: () => void;
}> = ({ actionType, icon, tooltip, onClick }) => {
  return (
    <Tooltip title={tooltip}>
      <AuthzButton
        roles={actionType === "view" ? "all" : ["owner", "iamOwner"]}
        type="text"
        size="small"
        aria-label={tooltip}
        icon={icon}
        onClick={onClick}
      />
    </Tooltip>
  );
};

const stopPropagation: React.MouseEventHandler = (e) => e.stopPropagation();

export const MonitorList: React.FC<{
  actionTypes: string[];
  showFindings?: boolean;
  showArchived?: boolean;
  variant?: "findingsTable" | "monitorList";
}> = ({
  actionTypes,
  showFindings = true,
  showArchived = false,
  variant = "monitorList",
}) => {
  const { details } = useContext(SelectedEnvironmentContext);
  const assessmentId = details?.assessmentId;
  const tenantId = useContext(Tenant);
  const navigate = useNavigateWithEnv();
  const { prioritized, findingParams, archived, loading } =
    useContext(FindingsContext);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [modalMonitor, setModalMonitor] = useState<MonitorWithMetadata>();
  const [archiveModalType, setArchiveModalType] = useState<
    "archive" | "unarchive"
  >("archive");

  const filteredMonitors = useMemo(() => {
    const output = showArchived ? archived : prioritized;

    const zeroesAtEnd = orderBy(output, [(item) => item.count > 0], ["desc"]);

    return zeroesAtEnd;
  }, [prioritized, showArchived, archived]);

  const monitorPath = useCallback(
    (item: MonitorWithMetadata) =>
      `${AppPaths.Posture}/monitors/${item.monitorId}?${findingParams}`,
    [findingParams]
  );

  const actionCallbacks = useMemo(
    () => ({
      view: (item: MonitorWithMetadata) =>
        navigate(monitorPath(item), {}, true),
      edit: (item: MonitorWithMetadata) => {
        setModalMonitor(item);
        setEditModalOpen(true);
      },
      template: (item: MonitorWithMetadata) => {
        const params = new URLSearchParams({
          templateId: item.monitorId,
          show: item.show,
        });
        navigate(`${AppPaths.Inventory}?${params.toString()}`, {}, true);
      },
      archive: (item: MonitorWithMetadata) => {
        setModalMonitor(item);
        setArchiveModalType("archive");
        setArchiveModalOpen(true);
      },
      unarchive: (item: MonitorWithMetadata) => {
        setModalMonitor(item);
        setArchiveModalType("unarchive");
        setArchiveModalOpen(true);
      },
    }),
    [navigate, monitorPath]
  );
  const handleClick = useCallback(
    (effectiveActionType: string, item: MonitorWithMetadata) => {
      return () => {
        actionCallbacks[
          effectiveActionType as
            | "archive"
            | "edit"
            | "template"
            | "unarchive"
            | "view"
        ](item);
      };
    },
    [actionCallbacks]
  );

  const actions = useMemo(
    () => (item: MonitorWithMetadata) => {
      const iconMap = {
        view: <EyeOutlined />,
        edit: <EditOutlined />,
        archive: <DeleteOutlined />,
        unarchive: <UndoOutlined />,
        template: <CopyOutlined />,
      };

      const tooltipMap = {
        view: "View",
        edit: "Edit",
        archive: "Archive",
        unarchive: "Unarchive",
        template: "Use as Template",
      };

      return actionTypes.map((actionType) => {
        if (actionType === "template" && item?.isCustom) {
          // Render invisible placeholder button to maintain consistent spacing
          return (
            <Tooltip key={actionType} title="">
              <Button
                type="text"
                size="small"
                icon={<span />}
                className="pointer-events-none invisible"
              />
            </Tooltip>
          );
        }

        // Use unarchive action type for archived monitors
        const effectiveActionType =
          actionType === "archive" && item?.archived ? "unarchive" : actionType;

        return (
          <MonitorActionButton
            key={actionType}
            actionType={
              effectiveActionType as
                | "archive"
                | "edit"
                | "template"
                | "unarchive"
                | "view"
            }
            icon={iconMap[effectiveActionType as keyof typeof iconMap]}
            tooltip={tooltipMap[effectiveActionType as keyof typeof tooltipMap]}
            onClick={handleClick(effectiveActionType, item)}
          />
        );
      });
    },
    [actionTypes, handleClick]
  );

  const archiveMonitor = useCallback(
    async (monitor: MonitorWithMetadata, unarchive = false) => {
      setArchiveLoading(true);
      try {
        const batch = writeBatch(DB);
        const assessmentPath = toAssessmentPath(tenantId, assessmentId ?? "");
        const monitorDocPath = `${assessmentPath}/monitors/${encodeURI(monitor.monitorId)}`;
        const isPreset = !monitor?.isCustom;

        if (isPreset) {
          // for preset monitors, the document may not exist yet
          batch.set(
            doc(DB, monitorDocPath),
            { archived: !unarchive },
            { merge: true }
          );
        } else {
          batch.update(doc(DB, monitorDocPath), {
            archived: !unarchive,
          });
        }

        await batch.commit();
        message.success(
          `Successfully ${unarchive ? "unarchived" : "archived"} monitor`
        );
      } catch (error) {
        message.error(
          `Failed to ${unarchive ? "unarchive" : "archive"} monitor: ${error}`
        );
      } finally {
        setArchiveLoading(false);
      }
    },
    [assessmentId, tenantId]
  );

  const renderMonitor = useCallback(
    (item: MonitorWithCount) => {
      return (
        <List.Item actions={actions(item)}>
          <List.Item.Meta
            title={
              <Flex>
                <LinkWithEnvironment
                  to={monitorPath(item)}
                  withTenant={true}
                  className="text-p0-primary-50!"
                >
                  {item.label}
                </LinkWithEnvironment>
              </Flex>
            }
            description={
              <Flex align="start" className="mr-[1em]">
                {item.isCustom && (
                  <StyledTag color={colors.tagColors.grey} bordered={false}>
                    Custom
                  </StyledTag>
                )}
                {/* Only Paragraph has the ellipsis prop, so its default bottom margin needs clearing */}
                <Spacing asChild mb={0}>
                  <Typography.Paragraph
                    /* eslint-disable-line react/forbid-component-props */ style={{
                      width: "90%",
                      color: colors.neutral["45"],
                    }}
                    ellipsis={{
                      rows: 1,
                      tooltip: true,
                    }}
                  >
                    {item.description}
                  </Typography.Paragraph>
                </Spacing>
              </Flex>
            }
          />
          <div className="w-[10em]">
            <Flex>
              <MonitorPriority monitor={item} />
            </Flex>
          </div>
          {showFindings && (
            <div className="w-[5em]">
              <StyledTag
                color={
                  item.count === 0
                    ? colors.tagColors.blue
                    : colors.tagColors.lightGrey
                }
                className="text-center"
              >
                {item.count}
              </StyledTag>
            </div>
          )}
        </List.Item>
      );
    },
    [actions, monitorPath, showFindings]
  );

  const tableColumns = useMemo(
    (): ResizableTableColumnsType<MonitorWithCount> => [
      {
        title: "",
        dataIndex: "show",
        key: "type",
        width: 32,
        preventResize: true,
        render: (_: unknown, record: MonitorWithCount) => (
          <MonitorTypeIcon show={record.show} />
        ),
      },
      {
        title: "Monitor",
        dataIndex: "label",
        key: "monitor",
        width: 300,
        render: (_: unknown, record: MonitorWithCount) => (
          <Flex align="center" gap={8}>
            {record.isCustom ? <P0BadgeSpacer /> : <P0Badge />}
            <div className="min-w-0">
              <Flex align="center" gap={6}>
                <Typography.Text strong ellipsis={{ tooltip: true }}>
                  {record.label}
                </Typography.Text>
                {record.fix && (
                  <StyledTag
                    color={colors.tagColors.blue}
                    icon={<ToolOutlined />}
                    className="me-0 shrink-0"
                  >
                    Fix Available
                  </StyledTag>
                )}
              </Flex>
              <Typography.Paragraph
                className="!mb-0 text-xs"
                /* eslint-disable-line react/forbid-component-props */ style={{
                  color: colors.neutral["45"],
                }}
                ellipsis={{ rows: 1, tooltip: true }}
              >
                {record.description}
              </Typography.Paragraph>
            </div>
          </Flex>
        ),
      },
      {
        title: "Severity",
        dataIndex: "priority",
        key: "severity",
        width: 100,
        preventResize: true,
        render: (_: unknown, record: MonitorWithCount) => (
          <MonitorPriority monitor={record} />
        ),
      },
      {
        title: (
          <Flex gap={4} align="center">
            Findings
            <Tooltip title="Number of open findings for this monitor across all scopes">
              <InfoCircleOutlined className="text-gray-400" />
            </Tooltip>
          </Flex>
        ),
        dataIndex: "count",
        key: "findings",
        width: 80,
        preventResize: true,
        render: (count: number) => (
          <StyledTag
            color={
              count === 0 ? colors.tagColors.blue : colors.tagColors.lightGrey
            }
            className="text-center"
          >
            {count}
          </StyledTag>
        ),
      },
      {
        title: "",
        key: "actions",
        width: 48,
        preventResize: true,
        render: (_: unknown, record: MonitorWithCount) => (
          <Flex gap={8} align="center" onClick={stopPropagation}>
            <Tooltip title="Archive">
              <AuthzButton
                roles={["owner", "iamOwner"]}
                type="text"
                aria-label="Archive"
                icon={
                  <span className="text-[1.2em]">
                    <InboxOutlined />
                  </span>
                }
                onClick={handleClick("archive", record)}
              />
            </Tooltip>
          </Flex>
        ),
      },
    ],
    [handleClick]
  );

  const onRow = useCallback(
    (record: MonitorWithCount) => ({
      onClick: () => navigate(monitorPath(record), {}, true),
    }),
    [navigate, monitorPath]
  );

  const closeEditModal = useCallback(() => setEditModalOpen(false), []);
  const closeArchiveModal = useCallback(() => setArchiveModalOpen(false), []);
  const handleUnarchive = useCallback(() => {
    if (!modalMonitor) return;
    archiveMonitor(modalMonitor, archiveModalType === "unarchive");
    setArchiveModalOpen(false);
  }, [archiveModalType, archiveMonitor, modalMonitor]);

  return (
    <>
      {variant === "findingsTable" ? (
        // The trailing per-row actions are only visible while the row is hovered.
        <ResizableTable<MonitorWithCount>
          className="[&_.ant-table-tbody>tr]:cursor-pointer! [&_.ant-table-thead>tr>th:first-child]:px-1.5! [&_.ant-table-thead>tr>th:first-child]:text-center! [&_.ant-table-tbody>tr>td:first-child]:px-1.5! [&_.ant-table-tbody>tr>td:first-child]:text-center! [&_.ant-table-tbody>tr>td:last-child>*]:invisible! [&_.ant-table-tbody>tr:hover>td:last-child>*]:visible!"
          tableId="posture-findings"
          columns={tableColumns}
          dataSource={filteredMonitors}
          rowKey="monitorId"
          loading={loading}
          onRow={onRow}
          pagination={false}
        />
      ) : (
        <List
          loading={loading}
          dataSource={filteredMonitors}
          renderItem={renderMonitor}
        />
      )}
      {actionTypes.includes("edit") && (
        <EditMonitorModal
          editModalOpen={editModalOpen}
          closeEditModal={closeEditModal}
          modalMonitor={modalMonitor}
          isCustom={modalMonitor?.isCustom ?? false}
        />
      )}
      {(variant === "findingsTable" ||
        actionTypes.includes("archive") ||
        actionTypes.includes("unarchive")) && (
        <Modal
          open={archiveModalOpen}
          footer={false}
          onCancel={closeArchiveModal}
        >
          <Typography.Title level={4}>Are you sure?</Typography.Title>
          {archiveModalType === "archive" ? (
            <Typography.Paragraph>
              This will archive this monitor and remove it from future
              assessments. You can unarchive this at a later date.
            </Typography.Paragraph>
          ) : (
            <Typography.Paragraph>
              This will re-activate this monitor and add it to future
              assessments.
            </Typography.Paragraph>
          )}
          <Typography.Paragraph code>
            {modalMonitor?.label}
          </Typography.Paragraph>
          <Button
            type="primary"
            danger={archiveModalType === "archive"}
            loading={archiveLoading}
            onClick={handleUnarchive}
          >
            {archiveModalType === "archive" ? "Archive" : "Re-activate"}
          </Button>
          <Button type="text" onClick={closeArchiveModal}>
            Cancel
          </Button>
        </Modal>
      )}
    </>
  );
};
